/**
 * Lista de mensagens.
 *
 * Três comportamentos precisam conviver aqui:
 *
 *   1. Rolar para o fim quando chega mensagem nova, mas só se o usuário já
 *      estava no fim. Arrastar alguém que está lendo o histórico de volta para
 *      baixo é a forma mais rápida de tornar a leitura impossível.
 *   2. Carregar mensagens antigas ao chegar no topo, preservando a posição
 *      visual. Sem compensar a altura inserida, o conteúdo salta sob o cursor.
 *   3. Agrupar mensagens seguidas do mesmo autor, para o histórico não virar
 *      uma parede de avatares repetidos.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ArrowDown } from 'lucide-react'
import { formatDayLabel, shouldGroupMessages, type Message } from '@nexus/shared'
import { cn } from '@/lib/cn'
import { useAppStore } from '@/store/app'
import { useChannelMessages } from '@/hooks/selectors'
import { MessageItem } from '@/components/chat/MessageItem'
import { Spinner } from '@/components/ui'

/** Distância do fim, em pixels, ainda considerada "no fim". */
const BOTTOM_THRESHOLD = 120
/** Distância do topo que dispara o carregamento do histórico. */
const TOP_THRESHOLD = 400

export function MessageList({
  channelId,
  /** Mensagem que a pessoa pediu para ver, vinda da lista de fixadas. */
  alvo,
  onAlvoAtendido,
}: {
  channelId: string
  alvo?: string | null
  onAlvoAtendido?: () => void
}) {
  const messages = useChannelMessages(channelId)
  const loading = useAppStore((s) => s.loadingChannels.has(channelId))
  const hasMore = useAppStore((s) => s.hasMoreBefore[channelId] !== false)
  const emTrecho = useAppStore((s) => s.emTrecho[channelId] === true)
  const loadOlder = useAppStore((s) => s.loadOlderMessages)
  const carregarAoRedor = useAppStore((s) => s.carregarAoRedor)
  const voltarAoPresente = useAppStore((s) => s.voltarAoPresente)
  const markRead = useAppStore((s) => s.markRead)

  const containerRef = useRef<HTMLDivElement>(null)
  const [atBottom, setAtBottom] = useState(true)

  /** Mensagem piscando agora, logo depois de ter sido alcançada. */
  const [destacada, setDestacada] = useState<string | null>(null)

  // O realce responde a uma ação que já terminou; deixar aceso vira sujeira.
  useEffect(() => {
    if (!destacada) return
    const relogio = setTimeout(() => setDestacada(null), 2000)
    return () => clearTimeout(relogio)
  }, [destacada])

  // Guarda a altura antes de carregar histórico, para compensar depois.
  const pendingRestore = useRef<{ scrollHeight: number; scrollTop: number } | null>(null)
  const lastMessageId = useRef<string | null>(null)
  const lastChannelId = useRef<string | null>(null)

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    const container = containerRef.current
    if (!container) return
    container.scrollTo({ top: container.scrollHeight, behavior })
  }, [])

  const handleScroll = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight
    const nowAtBottom = distanceFromBottom < BOTTOM_THRESHOLD
    setAtBottom(nowAtBottom)

    if (nowAtBottom) markRead(channelId)

    if (container.scrollTop < TOP_THRESHOLD && hasMore && !loading && messages.length > 0) {
      pendingRestore.current = {
        scrollHeight: container.scrollHeight,
        scrollTop: container.scrollTop,
      }
      void loadOlder(channelId)
    }
  }, [channelId, hasMore, loading, loadOlder, markRead, messages.length])

  // Troca de canal: vai direto para o fim, sem animação.
  useLayoutEffect(() => {
    if (lastChannelId.current !== channelId) {
      lastChannelId.current = channelId
      lastMessageId.current = null
      pendingRestore.current = null
      requestAnimationFrame(() => {
        scrollToBottom('auto')
        setAtBottom(true)
      })
    }
  }, [channelId, scrollToBottom])

  /*
    Levar a pessoa até uma mensagem específica.

    Ela pode já estar na tela, e aí é só rolar. Quando não está, o trecho ao
    redor dela é buscado no servidor e passa a ocupar a lista; o efeito roda de
    novo, agora achando o elemento. O realce existe porque a rolagem sozinha
    para no meio do histórico e deixa a pessoa procurando qual das mensagens à
    vista era a que ela pediu.
  */
  useEffect(() => {
    if (!alvo) return

    const elemento = containerRef.current?.querySelector<HTMLElement>(
      `[data-message-id="${alvo}"]`,
    )

    if (elemento) {
      elemento.scrollIntoView({ block: 'center', behavior: 'smooth' })
      setDestacada(alvo)
      onAlvoAtendido?.()
      return
    }

    let cancelado = false
    void carregarAoRedor(channelId, alvo).then((achou) => {
      // Sem sucesso não há o que rolar: a mensagem foi apagada, ou o canal
      // mudou embaixo da requisição.
      if (!achou && !cancelado) onAlvoAtendido?.()
    })

    return () => {
      cancelado = true
    }
  }, [alvo, channelId, messages, carregarAoRedor, onAlvoAtendido])

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container || messages.length === 0) return

    // Histórico recém-carregado: devolve a posição de leitura.
    if (pendingRestore.current) {
      const { scrollHeight, scrollTop } = pendingRestore.current
      const delta = container.scrollHeight - scrollHeight
      container.scrollTop = scrollTop + delta
      pendingRestore.current = null
      return
    }

    // Com uma mensagem a alcançar, mandar a lista para o fim seria desfazer, no
    // quadro seguinte, exatamente o que a pessoa pediu.
    if (alvo) return

    const newest = messages[messages.length - 1]!
    if (newest.id !== lastMessageId.current) {
      const isFirstRender = lastMessageId.current === null
      lastMessageId.current = newest.id

      if (isFirstRender || atBottom) {
        scrollToBottom(isFirstRender ? 'auto' : 'smooth')
      }
    }
  }, [messages, atBottom, alvo, scrollToBottom])

  // Mudanças de tamanho do container (abrir a lista de membros, redimensionar a
  // janela) não devem tirar quem estava no fim de lá.
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const observer = new ResizeObserver(() => {
      if (atBottom) scrollToBottom('auto')
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [atBottom, scrollToBottom])

  return (
    <div className="relative min-h-0 flex-1">
      <div ref={containerRef} onScroll={handleScroll} className="scroll-region h-full px-4 pb-4">
        {loading && messages.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <Spinner className="size-6 text-content-muted" />
          </div>
        ) : null}

        {!hasMore && messages.length > 0 ? <ChannelIntro channelId={channelId} /> : null}

        {loading && messages.length > 0 ? (
          <div className="flex justify-center py-3">
            <Spinner className="size-5 text-content-muted" />
          </div>
        ) : null}

        {messages.map((message, index) => {
          const previous = messages[index - 1]
          const grouped = shouldGroupMessages(previous, message)

          const showDaySeparator =
            !previous ||
            new Date(previous.createdAt).toDateString() !==
              new Date(message.createdAt).toDateString()

          return (
            <div key={message.id}>
              {showDaySeparator ? <DaySeparator date={message.createdAt} /> : null}
              <MessageItem
                message={message}
                grouped={grouped && !showDaySeparator}
                destacada={message.id === destacada}
              />
            </div>
          )
        })}
      </div>

      {!atBottom || emTrecho ? (
        <button
          type="button"
          onClick={() => {
            // Rolar não basta quando a lista é um pedaço do passado: o fim da
            // conversa nem está carregado.
            if (emTrecho) void voltarAoPresente(channelId)
            else scrollToBottom('smooth')
          }}
          className={cn(
            'absolute right-6 bottom-4 flex items-center gap-1.5 rounded-full',
            'bg-surface-overlay px-3 py-2 text-xs font-medium text-content-primary',
            'shadow-lg transition-colors hover:bg-surface-hover animate-slide-up',
          )}
        >
          <ArrowDown className="size-4" />
          {emTrecho ? 'Voltar para o presente' : 'Ir para o fim'}
        </button>
      ) : null}
    </div>
  )
}

function DaySeparator({ date }: { date: string }) {
  return (
    <div className="my-4 flex items-center gap-3 px-2">
      <div className="h-px flex-1 bg-line" />
      <span className="text-[11px] font-semibold text-content-muted">{formatDayLabel(date)}</span>
      <div className="h-px flex-1 bg-line" />
    </div>
  )
}

function ChannelIntro({ channelId }: { channelId: string }) {
  const channel = useAppStore((s) => s.channels[channelId])
  if (!channel) return null

  return (
    <div className="px-2 pt-8 pb-6">
      <div className="mb-3 flex size-16 items-center justify-center rounded-full bg-surface-overlay">
        <span className="text-3xl font-bold text-content-secondary">#</span>
      </div>
      <h2 className="text-2xl font-bold text-content-primary">Bem-vindo a #{channel.name}</h2>
      <p className="mt-1 text-content-secondary">
        {channel.topic ?? `Este é o começo do canal #${channel.name}.`}
      </p>
    </div>
  )
}

export type { Message }
